import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Badge,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useVideoUpload } from "../hooks/useVideoUpload";

/**
 * Refactored Upload Component
 *
 * A clean, focused component that:
 * - Uses custom hook for business logic
 * - Uses service layer for data operations
 * - Focuses only on UI presentation
 * - Provides better validation and error handling
 * - Enhanced user experience with improved visuals
 *
 * @component
 * @param {Object} props - Component props
 */

const UploadComponent = () => {
  const { t } = useTranslation();

  const {
    // Data
    videos,
    preferencesData,
    materialsData,

    // Loading states
    isLoading,
    isSubmitting,

    // Form state
    getFieldProps,
    getMultiSelectProps,
    errors,
    warnings,

    // Dialog state
    openDialog,
    selectedVideo,
    handleVideoEdit,
    handleNewVideo,
    closeDialog,

    // Form actions
    submitForm,

    // Notifications
    showSuccessSnackbar,
    showErrorSnackbar,
    errorMessage,
    closeSuccessSnackbar,
    closeErrorSnackbar,

    // Utilities
    hasMissingFields,
    getVideoThumbnail,
    deleteVideo,
    duplicateVideo,

    // Computed values
    isFormValid,
    hasWarnings,
  } = useVideoUpload();

  /** Grid of existing videos */
  const renderVideoGrid = () => (
    <Grid container spacing={2}>
      {videos.map((video) => (
        <Grid item key={video.id} xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: 320,
              cursor: "pointer",
              backgroundColor: hasMissingFields(video) ? "#fff3cd" : "white",
              border: hasMissingFields(video)
                ? "1px solid #ffeaa7"
                : "1px solid #e0e0e0",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              },
            }}
            onClick={() => handleVideoEdit(video)}
          >
            <Box sx={{ position: "relative" }}>
              {hasMissingFields(video) && (
                <Badge
                  color="warning"
                  variant="dot"
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                    },
                  }}
                >
                  <Box />
                </Badge>
              )}

              <CardMedia
                component="img"
                alt={video.title}
                height="180"
                image={getVideoThumbnail(video)}
                sx={{
                  objectFit: "cover",
                  filter: hasMissingFields(video) ? "opacity(0.8)" : "none",
                }}
              />

              {/* Status indicator */}
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {hasMissingFields(video) ? (
                  <Tooltip title={t("missingRequiredFields")}>
                    <ErrorIcon color="warning" fontSize="small" />
                  </Tooltip>
                ) : (
                  <Tooltip title={t("allFieldsComplete")}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </Tooltip>
                )}
              </Box>

              {/* Action buttons */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  display: "flex",
                  gap: 0.5,
                  opacity: 0,
                  transition: "opacity 0.2s ease-in-out",
                  ".MuiCard-root:hover &": {
                    opacity: 1,
                  },
                }}
              >
                <Tooltip title={t("duplicateVideo")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateVideo(video.id);
                    }}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("deleteVideo")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(t("confirmDeleteVideo"))) {
                        deleteVideo(video.id);
                      }
                    }}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      color: "error.main",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <CardContent sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    flexGrow: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {video.title || video.titleEnglish || t("untitledVideo")}
                </Typography>
                <EditIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", mt: 0.2 }}
                />
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "0.8rem",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.3,
                  mb: 1,
                }}
              >
                {video.description ||
                  video.descriptionEnglish ||
                  t("noDescription")}
              </Typography>

              {/* Preferences chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {video.preferences?.slice(0, 2).map((pref) => (
                  <Chip
                    key={pref.id}
                    label={pref.nameEnglish || pref.name}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                ))}
                {video.preferences?.length > 2 && (
                  <Chip
                    label={`+${video.preferences.length - 2}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  /** Dialog with form */
  const renderVideoDialog = () => (
    <Dialog
      open={openDialog}
      onClose={closeDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "70vh" },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {selectedVideo ? (
            <>
              <EditIcon />
              {t("editVideo")}
            </>
          ) : (
            <>
              <AddIcon />
              {t("uploadNewVideo")}
            </>
          )}
        </Box>
      </DialogTitle>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitForm();
        }}
      >
        <DialogContent>
          {/* Warnings */}
          {hasWarnings && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
              <Typography variant="subtitle2" gutterBottom>
                {t("validationWarnings")}:
              </Typography>
              {warnings.map((warning, index) => (
                <Typography key={index} variant="body2">
                  • {warning}
                </Typography>
              ))}
            </Alert>
          )}

          {/* English Version */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {t("englishVersion")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("videoTitleEnglish")}
                  fullWidth
                  {...getFieldProps("videoTitleEnglish")}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("vimeoUrlEnglish")}
                  fullWidth
                  placeholder="https://vimeo.com/123456789 or 123456789"
                  {...getFieldProps("vimeoUrlEnglish")}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("descriptionEnglish")}
                  fullWidth
                  multiline
                  rows={3}
                  {...getFieldProps("videoInstructionsEnglish")}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* German Version */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {t("germanVersion")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("videoTitleGerman")}
                  fullWidth
                  {...getFieldProps("videoTitleGerman")}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("vimeoUrlGerman")}
                  fullWidth
                  placeholder="https://vimeo.com/123456789 or 123456789"
                  {...getFieldProps("vimeoUrlGerman")}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("descriptionGerman")}
                  fullWidth
                  multiline
                  rows={3}
                  {...getFieldProps("videoInstructionsGerman")}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </Paper>

          <Divider sx={{ my: 2 }} />

          {/* Preferences and Materials */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="video-preference-label">
                  {t("videoPreferences")}
                </InputLabel>
                <Select
                  labelId="video-preference-label"
                  multiple
                  {...getMultiSelectProps("videoPreferences")}
                  renderValue={(selected) =>
                    selected
                      .map((id) => {
                        const pref = preferencesData.find((p) => p.id === id);
                        return pref?.nameEnglish || pref?.name || id;
                      })
                      .join(", ")
                  }
                  disabled={isSubmitting}
                >
                  {preferencesData.map((preference) => (
                    <MenuItem key={preference.id} value={preference.id}>
                      {preference.nameEnglish || preference.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.videoPreferences && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.videoPreferences}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="video-materials-label">
                  {t("videoMaterials")}
                </InputLabel>
                <Select
                  labelId="video-materials-label"
                  multiple
                  {...getMultiSelectProps("selectedMaterials")}
                  renderValue={(selected) =>
                    selected
                      .map((id) => {
                        const material = materialsData.find((m) => m.id === id);
                        return material?.nameEnglish || material?.name || id;
                      })
                      .join(", ")
                  }
                  disabled={isSubmitting}
                >
                  {materialsData.map((material) => (
                    <MenuItem key={material.id} value={material.id}>
                      {material.nameEnglish || material.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeDialog} color="inherit" disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting
              ? t("saving")
              : selectedVideo
              ? t("updateVideo")
              : t("uploadVideo")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );

  /** Loading */
  const renderLoadingState = () => (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={40} />
    </Box>
  );

  if (isLoading) {
    return <Container>{renderLoadingState()}</Container>;
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box
        sx={{
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {t("videoManagement")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewVideo}
          size="large"
        >
          {t("newVideo")}
        </Button>
      </Box>

      {/* Stats */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="primary">
              {videos.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("totalVideos")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="warning.main">
              {videos.filter(hasMissingFields).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("videosNeedingAttention")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="success.main">
              {videos.filter((v) => !hasMissingFields(v)).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("completeVideos")}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Video Grid */}
      {videos.length > 0 ? (
        renderVideoGrid()
      ) : (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
          <AddIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t("noVideosYet")}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t("getStartedByAddingVideo")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewVideo}
          >
            {t("addFirstVideo")}
          </Button>
        </Paper>
      )}

      {/* Video Dialog */}
      {renderVideoDialog()}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={closeSuccessSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSuccessSnackbar}
          severity="success"
          variant="filled"
        >
          {selectedVideo ? t("videoUpdatedSuccess") : t("videoUploadSuccess")}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={closeErrorSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeErrorSnackbar} severity="error" variant="filled">
          {errorMessage || t("videoUploadError")}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UploadComponent;
